import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, DEPT, fmtDateTime } from "@/lib/api";

const staffIcon = (on) => L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;border-radius:50%;background:${on ? "#10B981" : "#64748B"};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
  iconSize: [30, 30], iconAnchor: [15, 15],
});
const hospIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:6px;background:#0F172A;color:white;font:700 13px sans-serif;display:grid;place-items:center;border:2px solid white">H</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

export default function LiveMap() {
  const [d, setD] = useState({ staff: [], clients: [] });
  const load = () => api.get("/location/live").then((r) => setD(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Live Map</h1>
          <p className="text-sm text-slate-500 mt-1">Latest GPS position of staff clocked in today · refreshes every 30s</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="live-map-refresh-button"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
      </div>
      <div className="grid xl:grid-cols-[1fr_320px] gap-5">
        <div className="h-[560px] rounded-2xl overflow-hidden border bg-white" data-testid="live-map-container">
          <MapContainer center={[25.3176, 82.9739]} zoom={12} className="h-full w-full">
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {d.clients.map((c) => (
              <Marker key={c.id} position={[c.lat, c.lng]} icon={hospIcon}><Popup><b>{c.hospital_name}</b><br />{c.address}</Popup></Marker>
            ))}
            {d.staff.map((s) => (
              <Marker key={s.user_id} position={[s.lat, s.lng]} icon={staffIcon(s.on_duty)}>
                <Popup><b>{s.full_name}</b><br />{DEPT[s.department]}<br />Last seen {fmtDateTime(s.last_seen)}</Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <div className="bg-white rounded-2xl border p-4 space-y-2 max-h-[560px] overflow-y-auto" data-testid="live-staff-list">
          <div className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">On Field Today ({d.staff.length})</div>
          {d.staff.length === 0 && <div className="text-sm text-slate-500">Nobody has clocked in yet.</div>}
          {d.staff.map((s) => (
            <div key={s.user_id} className="rounded-lg border p-3" data-testid={`live-staff-${s.user_id}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${s.on_duty ? "bg-emerald-500 pulse-dot" : "bg-slate-400"}`} />
                <span className="font-semibold text-sm text-slate-900">{s.full_name}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{DEPT[s.department]} · {s.on_duty ? "On duty" : "Clocked out"}</div>
              <div className="text-[11px] text-slate-400 font-mono-num mt-0.5">{s.lat.toFixed(4)}, {s.lng.toFixed(4)} · {fmtDateTime(s.last_seen)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
